import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';

/**
 * Fancy number input wrapper.
 * */
const NumberInput = (props) => {
  const {
    className,
    name,
    id,
    inputClassName,
    onChange,
    value,
    icon,
    placeholder,
    description,
    isDisabled,
  } = props;
  const [isInputBlured, setIsInputBlured] = useState(true);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const hasIcon = !!icon;
  const hasDescription = !!description;
  const noneLabelStyle = {};
  const showNoneLabel = isInputBlured && !value;

  useEffect(() => {
    const onFocus = () => {
      containerRef.current.classList.add('ft-ni-focused');
      setIsInputBlured(false);
    };
    const onBlur = (event) => {
      const { relatedTarget } = event;

      if (
        !relatedTarget ||
        !relatedTarget.offsetParent ||
        event.relatedTarget.offsetParent.parentNode !== containerRef.current
      ) {
        containerRef.current.classList.remove('ft-ni-focused');
        setIsInputBlured(true);
      }
    };

    if (inputRef && inputRef.current) {
      inputRef.current.addEventListener('focus', onFocus);
      inputRef.current.addEventListener('blur', onBlur);
    }

    return () => {
      if (inputRef && inputRef.current) {
        inputRef.current.removeEventListener('focus', onFocus);
        inputRef.current.removeEventListener('blur', onBlur);
      }
    };
  }, []);

  const focusInput = () => {
    inputRef.current.focus();
  };
  const handleChange = (event) => {
    const value = event.target.value;
    const correctedValue = value.replace(/[^0-9]/g, ''); // remove all non-numeric characters

    if (value !== correctedValue) {
      event.target.value = correctedValue; // correct the value
    }

    onChange(correctedValue);
  };
  const resetTimer = () => {
    onChange(null);
    focusInput();
  };

  const dynamicallyAdjustingInputWidth = `${((+value).toString().length + 1) * 8}px`;

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex items-center space-between border border-gray-400 rounded-24.8 text-base px-4.2 h-12.2 cursor-text',
        className,
      )}
      onClick={focusInput}
    >
      {hasIcon && (
        <div className="pr-4.2" onClick={focusInput}>
          {icon}
        </div>
      )}
      {showNoneLabel && (
        <div
          className="flex"
          role="button"
          tabIndex={0}
          onClick={focusInput}
          style={noneLabelStyle}
        >
          <Icon id="horizontal_mini_line" />
        </div>
      )}
      <input
        ref={inputRef}
        tabIndex="0"
        id={id}
        className={cn(inputClassName, 'timer-input text-base')}
        name={name}
        type="text"
        pattern="\d*"
        value={value || ''}
        onChange={handleChange}
        placeholder={showNoneLabel ? undefined : placeholder}
        disabled={isDisabled}
        data-test-id={DATA_TEST_IDS.TIMER_INPUT}
        // set input width based on the number of digits set by the user
        style={{ width: dynamicallyAdjustingInputWidth }}
      />
      <div className="flex-1" role="button" tabIndex={-1} onClick={focusInput}>
        {hasDescription && !!value ? description : ''}
      </div>
      {!!value && (
        <ButtonWithIcon
          icon="remove_circle"
          isIcon
          isNaked
          aria-label="Close"
          className="text-gray-500 rounded-none"
          onClick={resetTimer}
          disabled={isDisabled}
        />
      )}
    </div>
  );
};

NumberInput.displayName = 'NumberInput';

NumberInput.propTypes = {
  // The container class for custom styling.
  className: PropTypes.string,
  // The id of the input.
  id: PropTypes.string,
  // The input class for custom styling.
  inputClassName: PropTypes.string,
  // The name for the input.
  name: PropTypes.string,
  // The value of the input
  value: PropTypes.number,
  // Handler method for dealing with input changes.
  onChange: PropTypes.func.isRequired,
  // The placeholder of the input.
  placeholder: PropTypes.string,
  // The icon to display in the left part of the input.
  icon: PropTypes.object,
  // The description text to display in the right part of the input.
  description: PropTypes.string,
  isDisabled: PropTypes.bool,
};

NumberInput.defaultProps = {
  className: null,
  id: null,
  inputClassName: null,
  name: null,
  value: undefined,
  placeholder: null,
  icon: null,
  description: null,
  isDisabled: false,
};

export default NumberInput;
